import type { MouseEvent } from "react";

import "./Caminho.css";

/**
 * Um degrau do caminho — e o endereço dele, **quando existe**.
 *
 * <p>`href` ausente não é esquecimento: há degrau que **nomeia um nível sem
 * levar a lugar nenhum**. "Produção" é um setor no mapa de navegação e não tem
 * tela; escrevê-lo como link mandaria a pessoa para o vazio, e omiti-lo faria o
 * caminho começar no meio.
 */
export interface PassoDoCaminho {
  rotulo: string;
  /** O endereço do nível. `null` ou ausente = degrau que nomeia, não leva. */
  href?: string | null | undefined;
}

interface Props {
  /**
   * Os degraus, **da raiz até a tela atual** — o último é onde se está.
   *
   * <p>Com menos de dois, o componente não desenha nada: um caminho de um
   * degrau só repete o título logo abaixo e não oferece para onde subir.
   */
  passos: readonly PassoDoCaminho[];
  /**
   * Subir um nível é **navegar**. O componente não conhece roteador — quem o
   * conhece é a tela, e é ela quem decide se isso é `navigate` ou recarregar.
   *
   * <p>Sem ele, os degraus com endereço continuam sendo links de verdade e o
   * navegador faz o trabalho: recarrega a página. É o degrau abaixo, não a
   * ausência de função.
   */
  aoNavegar?: ((href: string) => void) | undefined;
  /** Como o conjunto se anuncia a quem ouve a tela. */
  rotulo?: string;
}

/**
 * Onde se está, e como voltar um nível.
 *
 * <p>Pedido do PO no levantamento de frontend: *"saber onde se está e voltar um
 * nível"*. O que existia era um botão único — `← Voltar aos lotes`, no
 * cabeçalho do lote —, e um botão que sabe **um** destino não é um caminho: ele
 * não diz de que setor é a tela, não se aprofunda quando a hierarquia
 * aprofunda, e some no dia em que a tela é aberta por outro lugar.
 *
 * <h2>⚠️ Ele vive na ÁREA DE CONTEÚDO, não no cabeçalho do sistema</h2>
 *
 * <p>O cabeçalho de cima é enxuto por decisão do PO — marca, usuário, sair — e a
 * empresa **não** sobe para lá. O caminho fica acima do título da página, dentro
 * da coluna de conteúdo, onde ele se lê junto com o que nomeia.
 *
 * <h2>O último degrau NÃO é link</h2>
 *
 * <p>Ele é onde se está, não para onde ir. Um link que aponta para a página que
 * já está aberta é um clique que não faz nada, e num sistema em que se confere
 * material de três CNPJs, gesto que não faz nada é gesto que faz duvidar de que
 * a tela respondeu. Ele sai como `&lt;span aria-current="page"&gt;` — que é
 * também como o leitor de tela anuncia "esta é a página atual".
 *
 * <h2>O separador não é lido, e não é caractere de texto solto</h2>
 *
 * <p>`›` sai em `aria-hidden`: quem ouve a tela recebe uma lista ordenada de
 * links dentro de uma navegação nomeada, e a estrutura já diz o que o símbolo
 * desenha. Lido, ele viraria "sinal de maior que" entre cada palavra.
 *
 * <h2>⚠️ Ele NÃO vai ao papel</h2>
 *
 * <p>A garantia é do componente, em `Caminho.css`
 * (`@media print { .ui-caminho { display: none } }`) — a mesma escolha da
 * `Abas`. Deixá-la para cada tela é a lista de ocultos que a folha de etiquetas
 * já trocou por lista de permitidos: ela falha na direção perigosa, porque
 * ninguém confere o que saiu da impressora. Navegação impressa no topo de um
 * documento regulado é ruído que **parece** parte do documento.
 */
export function Caminho({ passos, aoNavegar, rotulo = "Caminho" }: Props) {
  /*
   * Menos de dois degraus: nada a desenhar.
   *
   * Não é economia de pixel. Um caminho de um degrau só diz o nome da tela, que
   * o título logo abaixo já diz — e não oferece nível nenhum para subir, que é
   * a metade do pedido. Desenhar uma linha que repete a de baixo e não leva a
   * lugar nenhum é como um elemento de navegação vira decoração.
   */
  if (passos.length < 2) return null;

  const ultimo = passos.length - 1;

  function aoClicar(evento: MouseEvent<HTMLAnchorElement>, href: string) {
    /*
     * Clique com modificador é do NAVEGADOR — abrir noutra aba, copiar o
     * endereço. É a mesma regra da `Abas`, e pela mesma razão: interceptar tudo
     * transformaria o `href` numa mentira.
     */
    if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return;
    if (evento.button !== 0) return;
    if (aoNavegar === undefined) return;
    evento.preventDefault();
    aoNavegar(href);
  }

  return (
    <nav className="ui-caminho" aria-label={rotulo}>
      {/* `<ol>` e não `<ul>`: a ordem É a informação. Uma lista sem ordem
          descreveria os mesmos degraus como um conjunto de irmãos. */}
      <ol className="ui-caminho__lista">
        {passos.map((passo, indice) => {
          const atual = indice === ultimo;
          const href = passo.href ?? null;

          return (
            <li
              /* A chave junta rótulo e posição: dois níveis podem repetir a
                 palavra (um módulo "Lotes" dentro de um setor "Lotes"), e o
                 índice sozinho remontaria a lista inteira a cada navegação. */
              key={`${indice}-${passo.rotulo}`}
              className={`ui-caminho__passo${atual ? " ui-caminho__passo--atual" : ""}`}
            >
              {indice > 0 && (
                <span className="ui-caminho__separador" aria-hidden="true">
                  ›
                </span>
              )}

              {atual ? (
                <span className="ui-caminho__aqui" aria-current="page">
                  {passo.rotulo}
                </span>
              ) : href !== null ? (
                <a
                  className="ui-caminho__degrau"
                  href={href}
                  onClick={(evento) => aoClicar(evento, href)}
                >
                  {passo.rotulo}
                </a>
              ) : (
                /* Sem endereço: nomeia o nível e não promete clique. Um `<a>`
                   sem `href` não recebe foco nem responde a Enter — seria um
                   link com aparência de link e nada por trás. */
                <span className="ui-caminho__nivel">{passo.rotulo}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
